import React, { useCallback } from 'react'
import { DetailReference, DetailType } from '~/types/state'
import { ExclamationCircle, Adjustments, Clipboard, Refresh, Folder } from '~/components/icons'

interface DetailCardDetailsProps {
  reference: DetailReference<DetailType>
}

interface DetailEntryProps {
  icon: JSX.Element
  content: JSX.Element
}

const DetailCardDetails = ({ reference }: DetailCardDetailsProps): JSX.Element => {
  const { type } = reference

  const DetailEntry = useCallback(({ icon, content }: DetailEntryProps): JSX.Element | null => {
    return (
      <div className="detail-card-entry">
        <div className="entry-icon">{icon}</div>
        <div className="entry-content">{content}</div>
        <style jsx>{`
          .detail-card-entry {
            display: flex;
            flex-direction: row;
            align-items: flex-start;
            margin-top: 6px;
            padding-right: 8px;
          }

          .entry-icon {
            min-width: 58px;
            padding-top: 4px;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: center;
          }

          .entry-content {
            flex-grow: 1;
            font-family: 'Space Mono', monospace;
            font-size: 10px;
            line-height: 20px;
            vertical-align: middle;
          }
        `}</style>
      </div>
    )
  }, [])

  switch (type) {
    case 'input': {
      const { input } = reference as DetailReference<'input'>
      return (
        <>
          <DetailEntry
            icon={<ExclamationCircle mode="dark" />}
            content={
              <div className="input-entry-description">
                {input.description ?? 'no description'}
                <style jsx>{`
                  .input-entry-description {
                    width: 100%;
                    font-family: 'Space Mono', monospace;
                    font-size: 12px;
                    line-height: 20px;
                  }
                `}</style>
              </div>
            }
          />
          <DetailEntry
            icon={<Adjustments mode="dark" />}
            content={
              <div className="input-entry-type">
                <div className="entry-type">{(input.annotations as any).type}</div>
                <div className="entry-type">
                  {(input as any)['default'] ?? <span className="no-default">no default</span>}
                </div>
                <style jsx>{`
                  .input-entry-type {
                    width: 100%;
                    height: 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                  }

                  .no-default {
                    opacity: 0.4;
                  }
                `}</style>
              </div>
            }
          />
        </>
      )
    }
    case 'output': {
      // const ref = reference as DetailReference<'output'>
      return <></>
    }
    case 'task': {
      const { task } = reference as DetailReference<'task'>
      return (
        <>
          <DetailEntry
            icon={<Clipboard mode="dark" />}
            content={
              <div className="entry-template">
                {task.template}
                <style jsx>{`
                  .entry-template {
                    text-align: left;
                  }
                `}</style>
              </div>
            }
          />
          <DetailEntry
            icon={<Refresh mode="dark" />}
            content={
              <div className="entry-loop">
                <div>loop</div>
                {task.loop ? <div>{(task.loop?.from as any)?.name}</div> : <div className="no-loop">no loop</div>}
                <style jsx>{`
                  .entry-loop {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                  }

                  .no-loop {
                    opacity: 0.4;
                  }
                `}</style>
              </div>
            }
          />
          <DetailEntry
            icon={<Folder mode="dark" />}
            content={
              <div className="input-entry-folder">
                <div className="entry-folder">subfolder</div>
                <div className="entry-folder">
                  {task.sub_folder ?? <span className="no-default">no subfolder</span>}
                </div>
                <style jsx>{`
                  .input-entry-folder {
                    width: 100%;
                    height: 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                  }

                  .no-default {
                    opacity: 0.4;
                  }
                `}</style>
              </div>
            }
          />
        </>
      )
    }
  }
}

export default DetailCardDetails
